/**
 * MKS-316 — the Attendance Report.
 *
 * The page used to open empty behind an Apply button and then show four
 * columns built from approved entries alone, which made anybody on approved
 * vacation read as absent. It now loads the current month on mount and answers
 * the three questions the report exists for — who worked, who was off, who is
 * missing hours — from a person × day grid plus a per-person summary.
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  useAttendanceReport,
  useDownloadAttendanceReportXlsx,
} from '../../hooks/useReports';
import { useProjects } from '../../hooks/useProjects';
import ReportFilters from '../../components/domain/ReportFilters';
import ReportTable from '../../components/domain/ReportTable';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import type {
  AttendanceCalendarDay,
  AttendanceEntryState,
  AttendanceReport,
  AttendanceRow,
} from '../../services/api/reports';

/** `#RRGGBB` → `#RRGGBB22`, the wash a cell gets behind the type's own colour. */
function tint(colour: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(colour) ? `${colour}22` : '#EEF2FF';
}

/** The current calendar month, the period the page opens on. */
export function currentMonthRange(now: Date = new Date()): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = now.getFullYear();
  const month = now.getMonth();
  const last = new Date(year, month + 1, 0).getDate();
  return {
    from: `${year}-${pad(month + 1)}-01`,
    to: `${year}-${pad(month + 1)}-${pad(last)}`,
  };
}

/** The sentinel the employee-type `<select>` uses for the people with no category. */
export const NO_EMPLOYEE_TYPE = '__none__';

export interface AttendanceRowFilters {
  /** Person search — matched against the display name and the email. */
  query: string;
  /** An exact `employeeTypeName`, `NO_EMPLOYEE_TYPE`, or '' for all. */
  employeeType: string;
  sort: 'name' | 'missing';
}

export const EMPTY_ROW_FILTERS: AttendanceRowFilters = {
  query: '',
  employeeType: '',
  sort: 'name',
};

/** The distinct employee types present in the rows, so the filter needs no second request. */
export function employeeTypeOptions(rows: AttendanceRow[]): string[] {
  const names = new Set<string>();
  for (const row of rows) {
    if (row.employeeTypeName) names.add(row.employeeTypeName);
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}

export function filterAttendanceRows(
  rows: AttendanceRow[],
  filters: AttendanceRowFilters
): AttendanceRow[] {
  const needle = filters.query.trim().toLowerCase();
  const matched = rows.filter((row) => {
    if (needle && !`${row.userName} ${row.email}`.toLowerCase().includes(needle)) return false;
    if (filters.employeeType) {
      const type = row.employeeTypeName ?? null;
      if (filters.employeeType === NO_EMPLOYEE_TYPE) {
        if (type !== null) return false;
      } else if (type !== filters.employeeType) {
        return false;
      }
    }
    return true;
  });

  if (filters.sort !== 'missing') {
    return [...matched].sort((a, b) => a.userName.localeCompare(b.userName));
  }
  // Most missing first. A person with no hours target (a contractor) has no
  // gap to rank, so they sort to the bottom rather than to the top next to the
  // people who are missing nothing.
  return [...matched].sort((a, b) => {
    const left = a.missingHours;
    const right = b.missingHours;
    if (left == null && right == null) return a.userName.localeCompare(b.userName);
    if (left == null) return 1;
    if (right == null) return -1;
    return right - left || a.userName.localeCompare(b.userName);
  });
}

/** One decimal, and no trailing `.0` on a whole number. */
function hours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** Mo/Tu/… initial for the grid header, derived from the UTC date key. */
function weekdayInitial(date: string): string {
  return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][new Date(`${date}T00:00:00.000Z`).getUTCDay()]!;
}

function DayCell({ row, day }: { row: AttendanceRow; day: AttendanceCalendarDay }) {
  const cell = (row.days ?? []).find((d) => d.date === day.date);
  const offDay = !day.isWorkingDay;
  const base = `border-b border-gray-100 px-1 py-1 text-center text-xs whitespace-nowrap ${
    offDay ? 'bg-gray-100 text-gray-400' : ''
  }`;

  if (cell && cell.timeOff.length > 0) {
    const slice = cell.timeOff[0]!;
    const share = slice.hours / 8;
    return (
      <td
        className={base}
        title={`${slice.typeName}${share < 1 ? ` — ${share.toFixed(2).replace(/\.?0+$/, '')} day` : ''}`}
        style={offDay ? undefined : { backgroundColor: tint(slice.colour) }}
      >
        <span className="font-medium" style={{ color: slice.colour }}>
          {slice.typeName.slice(0, 2).toUpperCase()}
          {share < 1 ? '½' : ''}
        </span>
      </td>
    );
  }

  if (!cell || cell.hours === 0) {
    return <td className={base}>{offDay ? '' : <span className="text-gray-300">·</span>}</td>;
  }

  const partlyUnapproved = cell.unapprovedHours > 0;
  return (
    <td
      className={base}
      title={
        partlyUnapproved
          ? `${hours(cell.hours)} h logged, ${hours(cell.unapprovedHours)} h not approved yet`
          : `${hours(cell.hours)} h approved`
      }
    >
      <span
        className={
          partlyUnapproved
            ? 'rounded bg-amber-100 px-1 italic text-amber-800 ring-1 ring-amber-300'
            : 'text-gray-800'
        }
      >
        {hours(cell.hours)}
      </span>
    </td>
  );
}

function AttendanceGrid({
  rows,
  calendar,
}: {
  rows: AttendanceRow[];
  calendar: AttendanceCalendarDay[];
}) {
  return (
    <div
      className="overflow-x-auto rounded-lg border border-gray-200 bg-white"
      data-testid="attendance-grid"
    >
      <table className="min-w-full border-collapse">
        <thead className="bg-gray-50">
          <tr>
            <th className="sticky left-0 z-20 bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Person
            </th>
            {calendar.map((day) => (
              <th
                key={day.date}
                title={day.holidayName ?? undefined}
                className={`px-1 py-2 text-center text-[10px] font-medium text-gray-500 ${
                  day.isWorkingDay ? '' : 'bg-gray-100'
                }`}
              >
                <div>{weekdayInitial(day.date)}</div>
                <div className={day.isHoliday ? 'text-red-500' : ''}>
                  {Number(day.date.slice(8, 10))}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.userId} className="group hover:bg-gray-50">
              <td className="sticky left-0 z-10 whitespace-nowrap border-b border-gray-100 bg-white px-4 py-1 text-sm text-gray-900 group-hover:bg-gray-50">
                {row.userName}
              </td>
              {calendar.map((day) => (
                <DayCell key={day.date} row={row} day={day} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTable({ rows, report }: { rows: AttendanceRow[]; report: AttendanceReport }) {
  const columns = [
    {
      key: 'user',
      header: 'Person',
      render: (row: AttendanceRow) => (
        <div>
          <span className="font-medium text-gray-900">{row.userName}</span>
          <span className="block text-xs text-gray-500">
            {row.employeeTypeName ?? row.email}
          </span>
        </div>
      ),
    },
    {
      key: 'workingDays',
      header: 'Working days',
      align: 'right' as const,
      render: (row: AttendanceRow) => (
        <span className="text-gray-700">{row.workingDays ?? 0}</span>
      ),
    },
    {
      key: 'daysWorked',
      header: 'Days worked',
      align: 'right' as const,
      render: (row: AttendanceRow) => <span className="text-gray-700">{row.daysWorked}</span>,
    },
    {
      key: 'daysOff',
      header: 'Days off',
      render: (row: AttendanceRow) =>
        (row.daysOffByType ?? []).length === 0 ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {(row.daysOffByType ?? []).map((type) => (
              <span
                key={type.typeId ?? type.typeName}
                className="rounded px-1.5 py-0.5 text-xs font-medium"
                style={{ backgroundColor: tint(type.colour), color: type.colour }}
              >
                {type.typeName} {type.days}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'logged',
      header: 'Logged',
      align: 'right' as const,
      render: (row: AttendanceRow) => (
        <span className="text-gray-700">
          {hours(row.totalHours)}
          {(row.unapprovedHours ?? 0) > 0 && (
            <span
              className="ml-1 italic text-amber-700"
              title={`${hours(row.unapprovedHours ?? 0)} h not approved yet`}
            >
              ({hours(row.unapprovedHours ?? 0)} unapproved)
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'expected',
      header: 'Expected',
      align: 'right' as const,
      render: (row: AttendanceRow) =>
        row.expectedHours == null ? (
          <span className="text-gray-400" title="No hours target — no employee contract in this period">
            —
          </span>
        ) : (
          <span className="text-gray-700">{hours(row.expectedHours)}</span>
        ),
    },
    {
      key: 'missing',
      header: 'Missing',
      align: 'right' as const,
      render: (row: AttendanceRow) =>
        row.missingHours == null ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span className={row.missingHours > 0 ? 'font-semibold text-red-600' : 'text-green-600'}>
            {hours(row.missingHours)}
          </span>
        ),
    },
  ];

  return (
    <div data-testid="attendance-summary">
      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(row) => row.userId}
        footer={
          <tr>
            <td className="px-6 py-3 text-sm font-semibold text-gray-900">Total</td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {report.totals.workingDays ?? 0}
            </td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {rows.reduce((sum, row) => sum + row.daysWorked, 0)}
            </td>
            <td className="px-6 py-3 text-sm font-semibold text-gray-900">
              {hours(rows.reduce((sum, row) => sum + (row.daysOff ?? 0), 0))} d
            </td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {hours(rows.reduce((sum, row) => sum + row.totalHours, 0))}
            </td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {hours(rows.reduce((sum, row) => sum + (row.expectedHours ?? 0), 0))}
            </td>
            <td className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
              {hours(rows.reduce((sum, row) => sum + (row.missingHours ?? 0), 0))}
            </td>
          </tr>
        }
      />
    </div>
  );
}

export default function AttendanceReportPage() {
  const navigate = useNavigate();
  // Seeded, not null: the period the page opens on is already selected, so
  // there is nothing for the reader to press before they see their month.
  const [dateRange, setDateRange] = useState(() => currentMonthRange());
  const [entryState, setEntryState] = useState<AttendanceEntryState>('all');
  const [projectId, setProjectId] = useState<number | null>(null);
  const [rowFilters, setRowFilters] = useState<AttendanceRowFilters>(EMPTY_ROW_FILTERS);

  const filters = useMemo(
    () => ({
      ...dateRange,
      entryState,
      ...(projectId !== null ? { projectIds: [projectId] } : {}),
    }),
    [dateRange, entryState, projectId]
  );

  const { data: report, isLoading, error } = useAttendanceReport(filters);
  const { data: projects } = useProjects({ state: 'running' });
  const download = useDownloadAttendanceReportXlsx();

  const updateRowFilters = useCallback((patch: Partial<AttendanceRowFilters>) => {
    setRowFilters((current) => ({ ...current, ...patch }));
  }, []);

  const allRows = useMemo(() => report?.rows ?? [], [report]);
  const typeOptions = useMemo(() => employeeTypeOptions(allRows), [allRows]);
  const visibleRows = useMemo(
    () => filterAttendanceRows(allRows, rowFilters),
    [allRows, rowFilters]
  );
  const calendar = report?.calendar ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/reports')}
            className="mb-1 text-sm text-gray-500 hover:text-gray-700"
          >
            &larr; Back to Reports
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Attendance Report</h1>
          <p className="text-sm text-gray-500">
            Who worked, who was off and who is missing hours in the period.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => download.mutate(filters)}
          isLoading={download.isPending}
          disabled={!report}
        >
          <ArrowDownTrayIcon className="mr-1 inline h-4 w-4" />
          Export XLSX
        </Button>
      </div>

      <ReportFilters
        onApply={setDateRange}
        isLoading={isLoading}
        initialFrom={dateRange.from}
        initialTo={dateRange.to}
      >
        <div>
          <label
            htmlFor="attendance-entry-state"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Hours
          </label>
          <select
            id="attendance-entry-state"
            value={entryState}
            onChange={(e) => setEntryState(e.target.value as AttendanceEntryState)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All logged</option>
            <option value="approved">Approved only</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="attendance-project"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Project
          </label>
          <select
            id="attendance-project"
            value={projectId ?? ''}
            onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All projects</option>
            {(projects ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.fullName || project.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="attendance-employee-type"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Employee type
          </label>
          <select
            id="attendance-employee-type"
            value={rowFilters.employeeType}
            onChange={(e) => updateRowFilters({ employeeType: e.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All types</option>
            {typeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            <option value={NO_EMPLOYEE_TYPE}>No type</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="attendance-person"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Person
          </label>
          <input
            id="attendance-person"
            type="search"
            value={rowFilters.query}
            placeholder="Search name or email"
            onChange={(e) => updateRowFilters({ query: e.target.value })}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="attendance-sort" className="mb-1 block text-sm font-medium text-gray-700">
            Sort
          </label>
          <select
            id="attendance-sort"
            value={rowFilters.sort}
            onChange={(e) =>
              updateRowFilters({ sort: e.target.value as AttendanceRowFilters['sort'] })
            }
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="name">Name</option>
            <option value="missing">Missing hours</option>
          </select>
        </div>
      </ReportFilters>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">{(error as Error).message}</p>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {report && !isLoading && visibleRows.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm font-medium text-gray-900">Nothing to show for this period</p>
          <p className="mt-1 text-sm text-gray-500">
            Nobody logged hours or took time off between {dateRange.from} and {dateRange.to} that
            matches the current filters. Try a wider period, clear the person or project filter, or
            switch Hours to &ldquo;All logged&rdquo; to include work that is not approved yet.
          </p>
        </div>
      )}

      {report && !isLoading && visibleRows.length > 0 && (
        <>
          <AttendanceGrid rows={visibleRows} calendar={calendar} />
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span>
              <span className="mr-1 rounded bg-amber-100 px-1 italic text-amber-800 ring-1 ring-amber-300">
                7.5
              </span>
              not approved yet
            </span>
            <span>
              <span className="mr-1 inline-block h-3 w-3 rounded-sm bg-gray-100 align-middle" />
              weekend or public holiday
            </span>
            <span>Two-letter codes are approved time off; ½ marks a partial day.</span>
          </div>
          <SummaryTable rows={visibleRows} report={report} />
        </>
      )}
    </div>
  );
}
